// src/components/common/ImageUpload.jsx
// Reusable Image Upload Component with Preview and Delete

import React, { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui-v2/button";
import { Card, CardContent } from "@/components/ui-v2/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui-v2/dialog";
import {
  Upload,
  X,
  Image as ImageIcon,
  Plus,
  Loader2,
  ZoomIn,
  FileText,
  Camera,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_MAX_FILES = 10;
const DEFAULT_MAX_SIZE_MB = 5;
const DEFAULT_ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

// ============================================================================
// IMAGE PREVIEW COMPONENT
// ============================================================================

const ImagePreview = ({ src, alt, onRemove, onPreview, isUploading, className }) => {
  return (
    <div
      className={cn(
        "relative group aspect-square rounded-lg overflow-hidden border bg-muted/50",
        className
      )}
    >
      {isUploading ? (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/80">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <img
            src={src}
            alt={alt || "Preview"}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = "/placeholder-image.png";
            }}
          />
          
          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            {onPreview && (
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => {
                  e.stopPropagation();
                  onPreview(src);
                }}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
            )}
            {onRemove && (
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ImageUpload = ({
  // Files state
  files = [],
  existingImages = [],
  onFilesChange,
  onRemoveExisting,
  
  // Config
  maxFiles = DEFAULT_MAX_FILES,
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  multiple = true,
  
  // UI Options
  label = "Upload Images",
  description,
  disabled = false,
  loading = false,
  showPreview = true,
  compact = false,
  gridCols = 4,
  
  // Camera support (for mobile)
  allowCamera = false,
  
  // Custom class
  className,
}) => {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [localPreviews, setLocalPreviews] = useState([]);

  // Total count including existing images
  const totalCount = (existingImages?.length || 0) + (files?.length || 0);
  const canAddMore = totalCount < maxFiles;
  const remainingSlots = maxFiles - totalCount;

  // ========== FILE VALIDATION ==========

  const validateFile = useCallback((file) => {
    // Check type
    if (!acceptedTypes.includes(file.type)) {
      toast.error(`Invalid file type: ${file.name}. Accepted: ${acceptedTypes.map(t => t.split('/')[1]).join(', ')}`);
      return false;
    }

    // Check size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSizeMB) {
      toast.error(`File too large: ${file.name}. Max size: ${maxSizeMB}MB`);
      return false;
    }

    return true;
  }, [acceptedTypes, maxSizeMB]);

  // ========== FILE HANDLING ==========

  const handleFiles = useCallback((newFiles) => {
    if (!canAddMore) {
      toast.error(`Maximum ${maxFiles} images allowed`);
      return;
    }

    const fileArray = Array.from(newFiles);
    const validFiles = fileArray.filter(validateFile);

    // Limit to remaining slots
    const filesToAdd = validFiles.slice(0, remainingSlots);

    if (filesToAdd.length < validFiles.length) {
      toast.warning(`Only ${remainingSlots} more image(s) can be added`);
    }

    if (filesToAdd.length > 0) {
      // Create local previews
      const newPreviews = filesToAdd.map(file => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      
      setLocalPreviews(prev => [...prev, ...newPreviews]);
      
      // Call parent handler
      if (onFilesChange) {
        onFilesChange([...(files || []), ...filesToAdd]);
      }
    }
  }, [canAddMore, maxFiles, remainingSlots, validateFile, files, onFilesChange]);

  // ========== DRAG & DROP ==========

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || loading) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [disabled, loading, handleFiles]);

  // ========== INPUT CHANGE ==========

  const handleInputChange = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    // Reset input
    e.target.value = "";
  }, [handleFiles]);

  // ========== REMOVE FILE ==========

  const removeFile = useCallback((index) => {
    // Revoke preview URL
    if (localPreviews[index]?.preview) {
      URL.revokeObjectURL(localPreviews[index].preview);
    }
    
    setLocalPreviews(prev => prev.filter((_, i) => i !== index));
    
    if (onFilesChange && files) {
      onFilesChange(files.filter((_, i) => i !== index));
    }
  }, [localPreviews, files, onFilesChange]);

  // ========== CLEANUP ==========

  React.useEffect(() => {
    return () => {
      // Cleanup preview URLs on unmount
      localPreviews.forEach(p => {
        if (p.preview) URL.revokeObjectURL(p.preview);
      });
    };
  }, []);

  // ========== RENDER ==========

  const gridClassName = {
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    5: "grid-cols-5",
    6: "grid-cols-6",
  }[gridCols] || "grid-cols-4";

  return (
    <div className={cn("space-y-3", className)}>
      {/* Label */}
      {label && (
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">
            {label}
            {maxFiles && (
              <span className="text-muted-foreground font-normal ml-2">
                ({totalCount}/{maxFiles})
              </span>
            )}
          </label>
        </div>
      )}

      {/* Description */}
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}

      {/* Existing Images Preview */}
      {showPreview && existingImages?.length > 0 && (
        <div className={cn("grid gap-3", gridClassName)}>
          {existingImages.map((url, index) => (
            <ImagePreview
              key={`existing-${index}`}
              src={url}
              alt={`Existing ${index + 1}`}
              onRemove={onRemoveExisting ? () => onRemoveExisting(url, index) : undefined}
              onPreview={setPreviewImage}
            />
          ))}
        </div>
      )}

      {/* New Files Preview */}
      {showPreview && localPreviews.length > 0 && (
        <div className={cn("grid gap-3", gridClassName)}>
          {localPreviews.map((item, index) => (
            <ImagePreview
              key={`new-${index}`}
              src={item.preview}
              alt={item.file?.name || `New ${index + 1}`}
              onRemove={() => removeFile(index)}
              onPreview={setPreviewImage}
            />
          ))}
        </div>
      )}

      {/* Upload Area */}
      {canAddMore && (
        <div
          className={cn(
            "relative border-2 border-dashed rounded-lg transition-colors",
            dragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25",
            disabled || loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-primary/50",
            compact ? "p-4" : "p-6"
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !disabled && !loading && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept={acceptedTypes.join(",")}
            multiple={multiple && remainingSlots > 1}
            onChange={handleInputChange}
            disabled={disabled || loading}
            className="hidden"
            capture={allowCamera ? "environment" : undefined}
          />

          <div className={cn(
            "flex flex-col items-center justify-center gap-2 text-center",
            compact ? "py-2" : "py-4"
          )}>
            {loading ? (
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            ) : (
              <div className="flex items-center gap-2">
                <Upload className="h-6 w-6 text-muted-foreground" />
                {allowCamera && (
                  <Camera className="h-6 w-6 text-muted-foreground" />
                )}
              </div>
            )}

            <div>
              <p className={cn(
                "font-medium",
                compact ? "text-sm" : "text-base"
              )}>
                {compact ? "Add images" : "Drag & drop images here"}
              </p>
              {!compact && (
                <p className="text-sm text-muted-foreground mt-1">
                  or click to browse
                </p>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              {acceptedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ')} • Max {maxSizeMB}MB each
              {remainingSlots < maxFiles && ` • ${remainingSlots} slot(s) remaining`}
            </p>
          </div>
        </div>
      )}

      {/* Add More Button (when images exist but can add more) */}
      {showPreview && totalCount > 0 && canAddMore && !compact && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || loading}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add More Images
        </Button>
      )}

      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Image Preview</DialogTitle>
          </DialogHeader>
          {previewImage && (
            <div className="flex items-center justify-center">
              <img
                src={previewImage}
                alt="Preview"
                className="max-h-[70vh] max-w-full object-contain rounded-lg"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// EXPORTS
// ============================================================================

export default ImageUpload;

export { ImagePreview };
